import { useEffect, useState } from 'react';
import { apiFetch } from '../config/api';

const SCREENED_TYPES = new Set(['visitor', 'parent_guardian']);

const STATUS_LABELS = {
  no_match: 'No match found within checked coverage',
  possible_match: 'Possible match — review required',
  unable_to_screen: 'Unable to screen',
  not_configured: 'Not configured',
};

function statusLabel(status) {
  return STATUS_LABELS[status] || 'Unable to screen';
}

export function screeningApplies(visitorType) {
  return SCREENED_TYPES.has(visitorType);
}

function backgroundOnly(screening) {
  return screening?.fbi_status === 'unable_to_screen' || screening?.fbi_status === 'not_configured';
}

export function screeningAllowsCheckIn(screening) {
  if (!screening || backgroundOnly(screening)) return true;
  if (screening.fbi_status === 'possible_match') {
    return screening.admission === 'allowed';
  }
  return screening.admission === 'allowed';
}

export default function VisitorScreeningPanel({
  token,
  schoolId,
  form,
  screening,
  onScreening,
  exceptionReason,
  onExceptionReason,
}) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [decision, setDecision] = useState('different_person');
  const [reason, setReason] = useState('');

  useEffect(() => {
    let active = true;
    apiFetch(`/visitors/screening-settings?school_id=${schoolId}`, {}, token)
      .then((response) => {
        if (active) setSettings(response.data);
      })
      .catch(() => {
        if (active) setSettings(null);
      });
    return () => {
      active = false;
    };
  }, [schoolId, token]);

  if (!screeningApplies(form.visitorType) || !screening || backgroundOnly(screening) || screening.admission === 'allowed') return null;

  const review = screening.fbi_status === 'possible_match';

  async function saveDecision() {
    if (!screening?.id) return;
    setBusy(true);
    setError('');
    try {
      const response = await apiFetch(`/visitors/screenings/${screening.id}/decision`, {
        method: 'POST',
        body: JSON.stringify({
          school_id: Number(schoolId),
          decision,
          reason,
        }),
      }, token);
      onScreening(response.data);
      setReason('');
    } catch (decisionError) {
      setError(decisionError.message || 'The review could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="vis-screening">
      <h3>Review required</h3>
      <div className="vis-screening-results">
        <p><strong>FBI public wanted notices:</strong> {statusLabel(screening.fbi_status)}</p>
        <p className="vis-scan-note">{screening.fbi_message}</p>
      </div>
      {review ? <div className="vis-alert vis-alert-warn" role="status"><strong>Review required</strong></div> : null}
      {review && screening.can_review ? (
        <div className="vis-screening-compare">
          {(screening.candidates || []).map((candidate, index) => (
            <article key={`${candidate.source_record_id || candidate.provider}-${index}`}>
              <h4>FBI public wanted notice</h4>
              <p>Scanned: {[form.firstName, form.middleName, form.lastName, form.suffix].filter(Boolean).join(' ')} · {form.birthDate || 'No birth date'}</p>
              <p>Source: {candidate.candidate_name || 'Name withheld'} · {(candidate.birth_dates || []).join(', ') || 'Birth date not supplied'}</p>
              <p>{candidate.jurisdiction || 'Jurisdiction not supplied'} · {candidate.category}</p>
              <p className="vis-scan-note">Matching basis: {candidate.matching_basis}. This is a possible match, not proof it is the same person.</p>
              {candidate.official_url ? (
                <a href={candidate.official_url} target="_blank" rel="noreferrer">View official source</a>
              ) : (
                <p>Official source link was not provided.</p>
              )}
            </article>
          ))}
          <div>
            <label>
              Administrator decision
              <select value={decision} onChange={(event) => setDecision(event.target.value)}>
                <option value="different_person">Different person / false match</option>
                <option value="unresolved">Unresolved — keep check-in on hold</option>
                <option value="identity_reviewed">Identity reviewed — apply school entry decision</option>
              </select>
            </label>
            <label>
              Reason
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={2} />
            </label>
            <button type="button" className="sa-btn sa-btn-secondary" onClick={saveDecision} disabled={busy || reason.trim().length < 3}>Save decision</button>
          </div>
        </div>
      ) : null}
      {screening && !review && screening.admission !== 'allowed' && settings?.screening_exception_enabled ? (
        <label>
          Administrator exception reason
          <textarea value={exceptionReason} onChange={(event) => onExceptionReason(event.target.value)} rows={2} />
        </label>
      ) : null}
      {error ? <div className="vis-alert vis-alert-block" role="alert">{error}</div> : null}
    </section>
  );
}
